import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../../app/hooks";
import { siteApiSlice } from "../../app/siteApiSlice";
import { setSharedAlert } from "../../app/siteSlice";
import { AlertLevel } from "../../components/AlertMessage";
import { ImpersonateResultModel } from "./impersonationApiSlice";

// Shared handling for starting or ending an impersonation. The impersonate
// endpoints return HTTP 200 even when the action is rejected, so success is
// determined by the IdentityResult's `succeeded` flag.
export function useImpersonationHandoff() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const showImpersonationError = useCallback(
		(errors: string[] | undefined, fallbackMessage: string) => {
			dispatch(
				setSharedAlert({
					level: AlertLevel.Error,
					message: errors && errors.length ? errors.join(" ") : fallbackMessage,
					showIcon: true,
				})
			);
		},
		[dispatch]
	);

	// On success, re-enter the SPA as the now-current user (the impersonated user,
	// or the impersonator when ending impersonation). We route through "/" so the
	// authenticated <Private> tree unmounts and remounts cleanly under the new
	// identity (its mount-time identity guard would otherwise bounce a mid-session
	// user swap to /login). Clearing the RTK Query cache and setting the
	// isLegacyLoggedIn flag lets Login.tsx run the shared, role-aware
	// getSelf/setUser/routing bootstrap - and Login shows a loader (not the form)
	// while it runs, so the login screen never flashes.
	const handleImpersonationResult = useCallback(
		(result: ImpersonateResultModel | undefined, fallbackMessage: string): boolean => {
			if (result?.succeeded) {
				dispatch(siteApiSlice.util.resetApiState());
				sessionStorage.setItem("isLegacyLoggedIn", "true");
				navigate("/");
				return true;
			}
			showImpersonationError(result?.errors, fallbackMessage);
			return false;
		},
		[dispatch, navigate, showImpersonationError]
	);

	return { handleImpersonationResult, showImpersonationError };
}
